import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import MinorCrashIcon from '@mui/icons-material/MinorCrash'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, VehicleDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { MichiganSOSBundlesParameters } from 'parameters/michiganSOSBundlesParameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'
import AddVehiclesDialog from './AddVehiclesDialog'

interface MichiganSOSBundlesManageVehiclesListCardProps {
  michiganSOSBundle: MichiganSOSBundleDto
  setMichiganSOSBundle: React.Dispatch<React.SetStateAction<MichiganSOSBundleDto>>
}

export default function MichiganSOSBundlesManageVehiclesListCard({
  michiganSOSBundle,
  setMichiganSOSBundle
}: React.PropsWithChildren<MichiganSOSBundlesManageVehiclesListCardProps>) {
  const layout = useLayout()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false,
    isAddingVehiclesToMichiganSOSBundle: false,
    isRemovingVehiclesFromMichiganSOSBundle: false
  })

  const [michiganOutgoingSOSBundlesTitlesManageVehiclesAddVehiclesDialogOpen, setMichiganSOSBundlesManageVehiclesDialogOpen] = useState<boolean>(false)

  const [parameters, setParameters] = useState<MichiganSOSBundlesParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'vin'
  })
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>([])

  const getMichiganSOSBundleVehicles = useDebounce((michiganSOSBundleGuid: string, parameters: MichiganSOSBundlesParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.michiganSoSBundles
      .getMichiganSOSBundleVehicles(michiganSOSBundleGuid, parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    console.log(michiganSOSBundle)
    if (michiganSOSBundle && michiganSOSBundle.michiganSOSBundleGuid) {
      getMichiganSOSBundleVehicles(michiganSOSBundle.michiganSOSBundleGuid, parameters)
    }
  }, [getMichiganSOSBundleVehicles, michiganSOSBundle, parameters])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isRemovingVehiclesFromMichiganSOSBundle)
  }, [loadingState.isRemovingVehiclesFromMichiganSOSBundle])

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters((prev: { orderBy: string; order: string }) => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVehiclesDialog
        isLoading={loadingState.isAddingVehiclesToMichiganSOSBundle}
        michiganSOSBundle={michiganSOSBundle}
        onClose={() => {
          setMichiganSOSBundlesManageVehiclesDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isAddingVehiclesToMichiganSOSBundle', true)
          controllers.michiganSoSBundles
            .addVehiclesToMichiganSOSBundle(michiganSOSBundle.michiganSOSBundleGuid!, values)
            .then(response => {
              enqueueSnackbar('Michigan SOS Bundle Vehicles Saved Successfully!', { variant: 'success' })
              setMichiganSOSBundlesManageVehiclesDialogOpen(false)
              setMichiganSOSBundle(response.value)
              getMichiganSOSBundleVehicles(michiganSOSBundle.michiganSOSBundleGuid!, parameters)
            })
            .finally(() => {
              setLoadingState('isAddingVehiclesToMichiganSOSBundle', false)
            })
        }}
        open={michiganOutgoingSOSBundlesTitlesManageVehiclesAddVehiclesDialogOpen}
      />
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Manage Vehicles
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  color='error'
                  disabled={selectedVehicles.length === 0}
                  endIcon={<DeleteForeverIcon />}
                  onClick={() => {
                    setLoadingState('isRemovingVehiclesFromMichiganSOSBundle', true)
                    controllers.michiganSoSBundles
                      .removeVehiclesFromMichiganSOSBundle(michiganSOSBundle.michiganSOSBundleGuid!, selectedVehicles)
                      .then(response => {
                        setSelectedVehicles([])
                        enqueueSnackbar('Vehicles  Removed Successfully!', { variant: 'success' })
                        setMichiganSOSBundle(response.value)
                      })
                      .finally(() => {
                        setLoadingState('isRemovingVehiclesFromMichiganSOSBundle', false)
                      })
                  }}
                  variant='text'
                >
                  REMOVE SELECTED
                </Button>

                <Button
                  color='primary'
                  endIcon={<MinorCrashIcon />}
                  onClick={() => {
                    setMichiganSOSBundlesManageVehiclesDialogOpen(true)
                  }}
                  variant='text'
                  // disabled={Boolean(michiganOutgoingTitle.dropOffDate)}
                >
                  ADD VEHICLES
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingVehicles && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingVehicles && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === totalCount && selectedVehicles.length !== 0}
                            // disabled={Boolean(michiganOutgoingTitle.dropOffDate) || selectedVehicles.length === 0}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                            onChange={() => {
                              if (selectedVehicles.length === totalCount) {
                                setSelectedVehicles([])
                              } else {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='vin'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          VIN
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='year'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Year
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='make'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Make
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='model'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Model
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='importerStockNumber'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Importer Stock#
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.map((vehicle, index) => (
                        <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              color='primary'
                              checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                              onChange={() => {
                                if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                  setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                } else {
                                  setSelectedVehicles([...selectedVehicles, vehicle])
                                }
                              }}
                            />
                          </TableCell>

                          <TableCell>{vehicle.vin}</TableCell>

                          <TableCell>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          <TableCell>{vehicle.importerStockNumber}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Remove Selected Button:**

Clicking this button removes all selected vehicles from the Manage Vehicles Card.

This button is enabled only when at least one vehicle is selected.

**Add Vehicles Button:**

Clicking this button opens the Add Vehicles dialog.

**Select Box:**

Clicking the checkbox selects the vehicle in that row.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
